export interface VendorApiHttpAgentSettings {
  /**
   * Keep sockets around even when there are no outstanding requests, so they can be used for future requests without having to reestablish a TCP connection.
   */
  keepAlive?: boolean;
  /**
   * When using the `keepAlive`, it specifies the initial delay for TCP Keep-Alive packets. It is ignored when the `keepAlive` is false.
   */
  keepAliveMs?: number;
  /**
   * Set `true` to enable legacy TLS renegotiation.
   */
  enableLegacyTlsRenegotiation?: boolean;
}

export interface VendorApiHttpAdvancedSettings {
  /**
   * Http based vendor API clients can recognize recoverable network errors. The `maxAttemptsOnRecoverableNetworkError` sets how many request
   * attempts can be done in case of a recoverable network error. When 1 is set, no request is resend on a recoverable network error.
   */
  maxAttemptsOnRecoverableNetworkError?: number;
  /**
   * When no response is received in the `requestTimeoutMs` milliseconds, the request is going to be considered as failed.
   */
  requestTimeoutMs?: number;
  /**
   * Http based vendor API clients can detect that the server is overloaded. When the client detects it, this parameter is specifying how long new overloaded
   * responses are not counted into the serverOverloadedMaxCount. All overloaded responses within this value are considered as a single overloaded occurrence.
   */
  serverOverloadedIntervalMs?: number;
  /**
   * Http based vendor API clients can detect that the server is overloaded. When the client detects it, it can wait for a while and resend
   * the original request. The `serverOverloadedMaxCount` sets how many times in row the server can be overloaded until the client is going to
   * give it up and destroy itself. Please note, the counter is reset on any successful response.
   */
  serverOverloadedMaxCount?: number;
  /**
   * Http based vendor API clients can detect that the server is overloaded. When the client detects it, it can wait for a while and resend
   * the original request. The `serverOverloadedMaxRequestAttempts` sets max request attempts when the server is overloaded. When
   * the `serverOverloadedMaxRequestAttempts` is exceeded, the request is rejected.
   */
  serverOverloadedMaxRequestAttempts?: number;
  /**
   * Http based vendor API clients can detect that the server is overloaded. When the client detects it, it can wait for a while and resend
   * the original request. The `serverOverloadedMaxWaitTimeMs` sets max total wait time in milliseconds.
   * Time to receive a response is not counted. Request is going to be rejected when the `serverOverloadedMaxWaitTimeMs` is exceeded.
   */
  serverOverloadedMaxWaitTimeMs?: number;
  /**
   * When a http based vendor API client receives a `429 Too many requests response`, it is going to wait for a while and resend the original request.
   * When the `tooManyRequestsWaitTimeMs` is not set and the server sends no `Retry-After` header value, the client will wait
   * a random time in range 0 to `tooManyRequestsWaitTimeRandomSpreadMs` milliseconds.
   */
  tooManyRequestsWaitTimeRandomSpreadMs?: number;
  /**
   * When a http based vendor API client receives a `429 Too many requests response`, it is going to wait for a while and resend the original request.
   * When the `tooManyRequestsWaitTimeMs` is set to a number, the client will wait for the `tooManyRequestsWaitTimeMs` milliseconds and
   * then resend the request. When it is not set, it will wait for a `Retry-After` header value, or a random time in range 0
   * to `tooManyRequestsWaitTimeRandomSpreadMs` milliseconds when the `Retry-After` header value is not set.
   */
  tooManyRequestsWaitTimeMs?: number | null;
}
